import clsx from "clsx";
import { formatInTimeZone } from "date-fns-tz";
import React, { ReactNode } from "react";
import { useDispatch } from "react-redux";

import Button from "@material-ui/core/Button";
import FailIcon from "@material-ui/icons/Close";
import DoneIcon from "@material-ui/icons/Done";
import ImageIcon from "@material-ui/icons/Image";
import MediaIcon from "@material-ui/icons/PermMedia";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DASH } from "common/constants";
import RiskLevel from "components/RiskLevel";
import EntryHeaderInfo from "components/VetExamOverlay/HorseInfoContainer/HorseInfo/EntryHeaderInfo";
import HorseInfoTable from "components/VetExamOverlay/HorseInfoContainer/HorseInfo/HorseInfoTable/HorseInfoTable";
import { insertCharToString } from "components/VetExamOverlay/HorseInfoContainer/HorseInfo/helper";
import { useVetExamContext } from "components/VetExamOverlay/VetExamProvider/VetExamProvider";
import { getCombinedLocalStateExam } from "components/VetExamOverlay/helper";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { setIsUseLastExamEntry } from "store/actions/vetExam";
import { transformViewDate } from "utils/date-utils";
import { getPersonFullName } from "utils/person";
import ExamActions from "./ExamActions";
import ExamHistoryNote from "./ExamHistoryNote";
import NestedMultipleBlock from "./NestedMultipleBlock";
import ViewMediaButton from "./ViewMediaButton/ViewMediaButton";
import { EVariantExamsHistory, getVariantsClassNames } from "./helper";

import useStyles from "./styles";

interface IProps {
  exam?: racehorse360.IHorseExam;
  variant?: EVariantExamsHistory;
  className?: string;
  withCloneExamButton?: boolean;
  completedExams?: racehorse360.IHorseExam[];
}

interface IExamData {
  id: string;
  facilityName: string;
  date: string;
  time: string;
  allMedia: ReactNode;
  tableContent: {
    "Reason for Exam": string;
    Vet: string;
    Decision: ReactNode | string;
    "Risk Level (ABC)": ReactNode;
    Conclusion: string;
  };
}

const getReasonExam = (exam: racehorse360.IHorseExam): string | null => {
  if (!exam.reason) return null;

  return exam.reason.code ===
    racehorse360.HorseExamReasonCode.HORSE_EXAM_REASON_CODE_TRAINER_REQUESTED
    ? `For Trainer Requested on ${transformViewDate(
        exam.completedDate,
        exam.facility?.timezone
      )}`
    : exam.reason.description;
};

const ExamHistoryItem = (props: IProps) => {
  const {
    exam,
    variant,
    className,
    withCloneExamButton = false,
    completedExams
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const isUseLastExamEntry = useAppSelector(
    state => state.vetExam?.isUseLastExamEntry
  );

  const vetExamContext = useVetExamContext();

  const { detailsClassName } = getVariantsClassNames(variant);

  const handleCloneExamValue = () => {
    const isExamLast = exam.id === completedExams[0].id;

    if (isExamLast && isUseLastExamEntry) return;

    if (!isExamLast && isUseLastExamEntry) {
      dispatch(setIsUseLastExamEntry(false));
    } else if (isExamLast && !isUseLastExamEntry) {
      dispatch(setIsUseLastExamEntry(true));
    }

    const { currentExam, setValues } = vetExamContext;

    setValues(getCombinedLocalStateExam(currentExam, exam));
  };

  const renderDecision = (result: racehorse360.HorseExamResult) => {
    switch (result) {
      case racehorse360.HorseExamResult.HORSE_EXAM_RESULT_PASSED:
        return (
          <span className={classes.decisionValue}>
            Passed
            <DoneIcon
              className={clsx(classes.decisionIcon, classes.doneIcon)}
            />
          </span>
        );
      case racehorse360.HorseExamResult.HORSE_EXAM_RESULT_FAILED:
        return (
          <span className={classes.decisionValue}>
            Failed
            <FailIcon
              className={clsx(classes.decisionIcon, classes.failIcon)}
            />
          </span>
        );
      default:
        return DASH;
    }
  };

  const preparedExam: IExamData = {
    id: exam.id,
    facilityName: exam.facility?.name?.toLowerCase(),
    date: exam.completedDate
      ? insertCharToString(
          transformViewDate(exam.completedDate, exam?.facility?.timezone, "PP"),
          ".",
          3
        )
      : DASH,
    time: formatInTimeZone(
      exam.completedDate,
      exam.facility?.timezone,
      "p zzz"
    ),
    allMedia: <div>test</div>,
    tableContent: {
      "Reason for Exam": getReasonExam(exam) || DASH,
      Vet:
        getPersonFullName(
          {
            firstName: exam.assignedUser?.firstName,
            lastName: exam.assignedUser?.lastName
          },
          "%f %L"
        ) || DASH,
      Decision: renderDecision(exam.result),
      "Risk Level (ABC)": (
        <RiskLevel status={exam.riskLevel} variant={variant} />
      ),
      Conclusion: exam.conclusion?.description || DASH
    }
  };

  const renderViewMediaComponent = (title: string, icon: ReactNode) => {
    return <ViewMediaButton icon={icon}>{title}</ViewMediaButton>;
  };

  const renderSideInfoHeaderButton = () => {
    if (variant === EVariantExamsHistory.DETAILS) {
      return <ExamActions exam={exam} />;
    }

    return renderViewMediaComponent(
      "View All Media",
      <MediaIcon className={clsx(classes.mediaIcon, detailsClassName)} />
    );
  };

  return (
    <div className={clsx(className, classes.examItem, detailsClassName)}>
      <EntryHeaderInfo
        variant={variant}
        date={preparedExam.date}
        facilityName={preparedExam.facilityName}
        extraInfo={preparedExam.time}
        sideInfoButton={renderSideInfoHeaderButton()}
      />
      {withCloneExamButton && (
        <Button
          className={classes.cloneExam}
          variant={"outlined"}
          classes={{
            label: classes.cloneExamLabel
          }}
          onClick={handleCloneExamValue}
        >
          <span>Clone Exam</span>
          <span className={classes.cloneExamWarning}>
            Replaces current data in form
          </span>
        </Button>
      )}
      <HorseInfoTable
        variant={variant}
        data={preparedExam.tableContent}
        rowHeight={32}
      />
      <NestedMultipleBlock<racehorse360.IHorseExamJog>
        variant={variant}
        name="Jog Information"
        accordionTitle="View All Jog Info Entries"
        data={exam.jogs}
        baseFields={[
          "jogLimb",
          "customJogLimb",
          "jogScore",
          "jogInfo",
          "customJogInfo",
          "jogCondition",
          "customJogCondition"
        ]}
        value={renderViewMediaComponent(
          "View Media",
          <ImageIcon className={clsx(classes.imageIcon, detailsClassName)} />
        )}
      />
      <NestedMultipleBlock<
        racehorse360.IHorseExamDetail,
        racehorse360.IHorseExamDetailObservationEntry
      >
        className={classes.examDetails}
        variant={variant}
        name="Exam Details"
        accordionTitle="View All Exam Details"
        data={exam.examDetails}
        subEntriesFieldName={"observationEntries"}
        baseFields={[
          "detailLimb",
          "customDetailLimb",
          "detailLocation",
          "customDetailLocation",
          "detailStructure",
          "customDetailStructure",
          "detailLasixCCs"
        ]}
        subFields={[
          "detailObservation",
          "customDetailObservation",
          "detailSeverity",
          "detailCondition",
          "customDetailCondition"
        ]}
        value={renderViewMediaComponent(
          "View Media",
          <ImageIcon className={clsx(classes.imageIcon, detailsClassName)} />
        )}
      />
      <ExamHistoryNote
        variant={variant}
        title="Internal Note"
        content={exam.note || DASH}
      />
      <ExamHistoryNote
        className={classes.trainerComment}
        variant={variant}
        title="Comment to Trainer"
        content={exam.commentToTrainer?.body || DASH}
      />
    </div>
  );
};

export default ExamHistoryItem;
